import React, {useEffect, useState} from 'react';
import {pdfjs} from 'react-pdf';
import classnames from 'classnames';
import * as _ from 'lodash';
import {getFullDataURI} from '../../../utils/files/getFullDataURI';
import {getSelectedOptionLabel} from '../../../utils/options';
import {floorOptions, lotPropertyFloorOptions} from '../../../constants/options';
import {propertyTypesGroups as PT} from '../../../constants/propertyTypesGroups';
import styles from './styles.module.scss';

const imagesPerRow = 2;

const Floor = ({files, label, className, onImagesLoaded}) => {
  const [pdfPages, setPdfPages] = useState([]);
  useEffect(() => {
    const pdfFiles = files.filter(file => file.file_extension === 'pdf');

    pdfFiles.forEach(async file => {
      const src = getFullDataURI({
        body: file.body,
        file_extension: file.file_extension,
      });

      const pdf = await pdfjs.getDocument(src).promise;
      const pages = [];

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        const page = await pdf.getPage(pageNumber);
        const viewport = page.getViewport({scale: 2});

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          viewport,
        }).promise;

        pages.push(canvas.toDataURL('image/jpeg', 0.9));
      }

      setPdfPages(prev => [
        ...prev,
        {
          fileName: file.file_name,
          freeText: file.free_text,
          pages,
        },
      ]);

      onImagesLoaded?.();
    });
  }, [files]);

  const mapImages = () => {
    const items = [
      ...files
        .filter(file => file.file_extension !== 'pdf')
        .map(file => ({
          src: getFullDataURI({
            body: file.body,
            file_extension: file.file_extension,
          }),
          fileName: file.file_name,
          freeText: file.free_text,
        })),

      ...pdfPages.flatMap(pdf =>
        pdf.pages.map(page => ({
          src: page,
          fileName: pdf.fileName,
          freeText: pdf.freeText,
        }))
      ),
    ];

    return items.map((item, index) => (
      <div
        className={classnames(
          styles.imageItem,
          styles.innerPadding,
        )}
        key={`${item.fileName}-${index}`}
      >
        <div className={styles.imageWrapper}>
          <div
            className={classnames(
              styles.freeText,
              styles.bold,
              styles.textRegular
            )}
          >
            {item.freeText}
          </div>

          <img
            src={item.src}
            alt={item.fileName}
            className={styles.floorImage}
            onLoad={onImagesLoaded}
          />
        </div>
      </div>
    ));
  };

  return (
    <div className={classnames(styles.floorContainer, className)}>
      {Boolean(label) && (label !== 'undefined') && (
        <div className={classnames(styles.label, styles.textRegular, styles.innerPadding)}>
          {label}
        </div>
      )}
      <div className={styles.images}>
        {mapImages()}
      </div>
    </div>
  );
};

const getImagesSortedByFloor = (context, fileTypeID) => context.files
  .filter(file => file.file_type_id === fileTypeID)
  .sort((prev, next) => {
    if (prev.free_id > next.free_id) {
      return 1;
    }
    if (prev.free_id < next.free_id) {
      return -1;
    }
    return 0;
  });

const FloorsHeader = ({header}) => (
  <div className={classnames(
    styles.bold,
    styles.marginVertFloors,
    styles.innerPadding,
    styles.textRegular
  )}>
    {header}
  </div>
);

const mapFloors = imagesGroupedByFloor => (label) => {
  const images = imagesGroupedByFloor[label];
  const rows = _.chunk(images, imagesPerRow);

  return rows
    .map((row, index) => (
      <Floor
        files={row}
        label={(index === 0) && label}
        className={(index !== rows.length - 1) ? styles.floorRow : ''}
      />
    ));
};

const buildFloorLabel = (image, options) => {
  const freeID = parseInt(image.free_id, 10);
  const labelText = getSelectedOptionLabel(image.free_id, options);
  if (freeID === 100) { // not relevant option
    return '';
  } else {
    return (freeID <= 3) ? `קומה ${labelText}` : labelText;
  }
}

export const getFloors = (context, fileTypeID, header, customHeader) => {
  const options = PT.GROUND_OR_LOT.includes(context.data.pdfData.propertyTypeID)
    ? lotPropertyFloorOptions
    : floorOptions;
  const imagesSortedByFloor = getImagesSortedByFloor(context, fileTypeID);
  const imagesWithNamedFloor = imagesSortedByFloor
    .filter(item => item.free_id !== 100)
    .map(image => {
    const floorLabel = buildFloorLabel(image, options);
    return {...image, floorLabel};
  });
  const imagesGroupedByFloor = _.groupBy(imagesWithNamedFloor, 'floorLabel');
  const floorLabels = Object.keys(imagesGroupedByFloor);
  const isFileType10Exists = Array.isArray(context?.files)
    ? context.files.filter(item => item?.file_type_id === 10).length > 0
    : false;

  return floorLabels.map(mapFloors(imagesGroupedByFloor))
    .flat()
    .map((row, index) => {
      return (index === 0)
        ? (
          <div>
            {header && <FloorsHeader header={header}/>}
            {!isFileType10Exists ? customHeader : null}
            <div style={{paddingBottom: '30px'}}>{row}</div>
          </div>
        )
        : <div style={{paddingBottom: '30px'}}>{row}</div>;
    });
};
